import cn from 'classnames';
import * as AuthSelectors from 'domains/auth/auth.selectors';
import { PaymentProcessingStateEnum } from 'domains/payments/processing/interfaces.processing';
import * as paymentProcessingSelectors from 'domains/payments/processing/selectors.processing';
import * as productSelectors from 'domains/products/products.selectors';
import type { ProductsTypeV3 } from 'domains/products/products.types';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector, useStore } from 'react-redux';

import type { ProductMiniProps } from 'components/domains/payments/ProductMini';

import type { AppState } from 'redux-store';

import styles from './styles.module.scss';

export interface ProductsListProps {
  component: React.JSXElementConstructor<ProductMiniProps>;
}

const MONTH_PRICE = 12.99;

export const ProductsList: React.FC<ProductsListProps> = ({
  component: Component,
}) => {
  const store = useStore();
  const router = useRouter();
  const experimentOnlyBuyProduct = !!router.query.only_buy;
  let products = useSelector(productSelectors.getProductsSelector);

  if (experimentOnlyBuyProduct) {
    products = products.filter(({ is_current }) => !is_current);
  }

  const productForPayment = useSelector(
    paymentProcessingSelectors.productSelector,
  );
  const isAuthenticated = useSelector(AuthSelectors.isAuthSelector);
  const selected = router.query.selected as string | undefined;
  const selectedId = selected ? +selected : null;
  const defaultProduct =
    selectedId ||
    products.find((product) => !product.is_paid && product.prices[0].interval === 'year')
      ?.id ||
    products.find(
      (product) => !product.is_paid && product.prices[0].interval === 'lifetime',
    )?.id ||
    null;
  const [productId, setProductId] = useState(defaultProduct);
  const paidProduct = useSelector(productSelectors.getPaidProductsSelector);
  const product = useSelector((state: AppState) =>
    // @ts-ignore
    productSelectors.getProductSelector(state, productId),
  );
  const onClickHandler = useCallback(
    async (product: ProductsTypeV3) => {
      setProductId(product.id);
      (
        await import('domains/payments/processing')
      ).paymentsProcessingService.setProductById({ id: product.id, store });
    },
    [setProductId, store],
  );

  useEffect(() => {
    async function fn(): Promise<void> {
      if (!product || productForPayment) {
        return;
      }

      if (isAuthenticated && selectedId) {
        (
          await import('domains/payments/processing')
        ).paymentsProcessingService.showModal({
          store,
          product,
          state: PaymentProcessingStateEnum.CARD,
        });

        return;
      }

      (
        await import('domains/payments/processing')
      ).paymentsProcessingService.showModal({ store, product });
    }

    fn();
  }, [product, store, isAuthenticated, selectedId]);

  const productTitle = useMemo(() => {
    let component = null;

    if (
      product &&
      product.prices[0].interval === 'year' &&
      paidProduct &&
      paidProduct.prices[0].interval === 'month'
    ) {
      component = (
        <span>
          Save{' '}
          <span className={styles.diffPrice}>
            {(
              MONTH_PRICE * 12 -
              Number(product.prices[0].discount_price || product.prices[0].price)
            ).toFixed(2)}{' '}
            $
          </span>{' '}
          on your next Premium year
        </span>
      );
    } else if (
      product &&
      product.prices[0].interval === 'lifetime' &&
      paidProduct &&
      paidProduct.prices[0].interval === 'year'
    ) {
      component = (
        <span>
          Enjoy all current and upcoming Premium features, now and forever
        </span>
      );
    }

    return component;
  }, [product, paidProduct]);
  const render = products.map(item => {
    return (
      <Component
        isSelected={productId === item.id}
        key={item.id}
        product={item}
        onClick={onClickHandler}
      />
    );
  });

  return (
    <div
      className={cn(styles.container, {
        [styles.containerWithTitle]: productTitle,
      })}
    >
      {productTitle ? <div className={styles.title}>{productTitle}</div> : null}
      <div className={styles.content}>{render}</div>
    </div>
  );
};
